// Computes medication alerts (Rx pending doses + PRN interval gating).
// All times are operated as local HH:MM strings; dates use the program's
// shift.date (YYYY-MM-DD) for the current shift's pending Rx check.

// Parse "HH:MM" -> minutes since 00:00 (or null).
export function hmToMin(hm) {
  if (!hm || typeof hm !== "string") return null;
  const m = hm.match(/^(\d{2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

// Parse history entry's sortable_ts ("YYYY-MM-DD HH:MM") into a Date (UTC-safe local).
function parseHistoryTs(entry) {
  const ts = (entry.sortable_ts || "").trim();
  if (!ts) return null;
  const m = ts.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2] - 1;
  const d = +m[3];
  const h = m[4] ? +m[4] : 0;
  const mi = m[5] ? +m[5] : 0;
  return new Date(y, mo, d, h, mi, 0);
}

// Build the "latest administered" map per med_id from history.
export function lastAdministeredByMed(history) {
  const map = {};
  for (const h of history || []) {
    const id = h.med_id;
    if (!id) continue;
    if (!map[id]) {
      map[id] = { ...h, date: parseHistoryTs(h) };
    }
  }
  return map;
}

// For an Rx med, returns its scheduled doses status for the CURRENT shift:
// [{ scheduled: "08:00", status: "given"|"pending"|"upcoming", givenEntry? }]
// "given"   = found in `givenInShift` (loose match by med_id + time +/- 30 min)
// "pending" = scheduled time has passed (vs `nowMin`) but no record found
// "upcoming"= scheduled time is later than now
export function rxStatusForShift(med, givenInShift, nowMin) {
  const out = [];
  for (const t of med.schedule_times || []) {
    const sched = hmToMin(t);
    if (sched === null) continue;
    // Tolerance: within 60 minutes
    const matched = (givenInShift || []).find((g) => {
      if (g.med_id !== med.id) return false;
      const gm = hmToMin(g.time);
      if (gm === null) return false;
      return Math.abs(gm - sched) <= 60;
    });
    let status = "upcoming";
    if (matched) status = "given";
    else if (sched <= nowMin) status = "pending";
    out.push({ scheduled: t, status, givenEntry: matched || null });
  }
  return out;
}

// Aggregate Rx pending doses across the whole catalog for the current shift.
// Returns [{ med, scheduled, delayMinutes }] entries that are status="pending".
export function pendingRxDoses(catalog, givenInShift, nowMin) {
  const result = [];
  for (const med of catalog || []) {
    if ((med.default_indication || "rx") !== "rx") continue;
    if (!med.schedule_times?.length) continue;
    const states = rxStatusForShift(med, givenInShift, nowMin);
    for (const s of states) {
      if (s.status === "pending") {
        const schedMin = hmToMin(s.scheduled) ?? 0;
        result.push({
          med,
          scheduled: s.scheduled,
          delayMinutes: Math.max(0, nowMin - schedMin),
        });
      }
    }
  }
  // Sort by delay desc (most delayed first - higher priority)
  result.sort((a, b) => b.delayMinutes - a.delayMinutes);
  return result;
}

// Format a delay in minutes as "47 min" or "2 h 15 min".
export function formatDelay(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h} h` : `${h} h ${rem} min`;
}

// PRN availability for a med based on min_interval_hours and the last
// administration (from cross-program history). Returns:
//   { allowed: bool, hoursSinceLast: number|null, hoursUntilNext: number|null,
//     lastEntry: object|null }
// If the med has no min_interval_hours configured, always allowed.
export function prnAvailability(med, lastAdministered, now = new Date()) {
  if ((med.default_indication || "rx") !== "prn") {
    return { allowed: true, hoursSinceLast: null, hoursUntilNext: null, lastEntry: null };
  }
  const interval = Number(med.min_interval_hours || 0);
  const lastEntry = lastAdministered?.[med.id] || null;
  if (!lastEntry || !lastEntry.date || !interval) {
    return { allowed: true, hoursSinceLast: null, hoursUntilNext: null, lastEntry };
  }
  const ms = now.getTime() - lastEntry.date.getTime();
  const hoursSinceLast = ms / 36e5;
  const hoursUntilNext = Math.max(0, interval - hoursSinceLast);
  return {
    allowed: hoursSinceLast >= interval,
    hoursSinceLast,
    hoursUntilNext,
    lastEntry,
  };
}

export function formatHours(h) {
  if (h === null || h === undefined || !Number.isFinite(h)) return "-";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 10) return `${h.toFixed(1)} h`;
  return `${Math.round(h)} h`;
}
